import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { AppBar, useAuth0, useFlags } from "@inductiveautomation/ws-components";

import { AccountAppBarProps } from "./AccountAppBar.types";
import Navigation from "../Navigation/Navigation";
import { useCurrentUser } from "../../hooks/currentUser";
import { useGetProfileQuery } from "../../routes/Profile/api/profile.api";
import {
  retrieveAuthChannel,
  CHANNEL_MESSAGE_TYPES,
} from "../../utilities/broadcastChannel";
import { AccountFlags } from "../../featureFlags.ts";

const AccountAppBar = ({ title }: AccountAppBarProps) => {
  const { t } = useTranslation();
  const { loginWithRedirect, logout } = useAuth0();
  const navigate = useNavigate();
  const { data } = useGetProfileQuery();
  const { sendAuthChannelMessage } = retrieveAuthChannel();

  const userInfo = useCurrentUser();
  const { tsAccountWorkspaceVisibilityFe, tsAccountPublicProfileFe } =
    useFlags<AccountFlags>();

  const [isOpen, setIsOpen] = useState(false);
  const onSignOut = () => {
    void logout({
      logoutParams: {
        returnTo: window._acctConfig?.AUTH0_LOGOUT_URL,
      },
    }).then(() => {
      sendAuthChannelMessage(CHANNEL_MESSAGE_TYPES.LOGOUT);
    });
  };

  const onManageAccount = () => {
    if (window.location.pathname === "/") {
      window.location.reload();
    } else {
      void navigate("/");
    }
  };

  const onNavigationMenuClick = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <AppBar
        user={
          userInfo.user && !userInfo.isLoading
            ? {
                ...userInfo.user,
                publicProfileEnabled: tsAccountPublicProfileFe
                  ? data?.publicProfile.isEnabled
                  : false,
              }
            : undefined
        }
        loading={userInfo.isLoading}
        title={title}
        onNavigationMenuClick={onNavigationMenuClick}
        actions={{
          onManageAccount,
          onViewProfile: () =>
            void navigate(`/${data?.publicProfile.publicId}`),
          onSignIn: () => void loginWithRedirect(),
          onSignOut,
        }}
        translations={{
          accountTooltip: t("app_bar.account_tooltip"),
          appServicesTooltip: t("app_bar.apps_services_tooltip"),
          signIn: t("app_bar.sign_in"),
          account: {
            manageAccount: t("app_bar.manage_account"),
            viewProfile: t("app_bar.view_profile"),
            helpCenter: t("app_bar.help_center"),
            signOut: t("app_bar.sign_out"),
          },
          services: {
            community_forums: t("app_bar.services.community_forums"),
            customer_feedback: t("app_bar.services.customer_feedback"),
            inductive_automation: t("app_bar.services.inductive_automation"),
            inductive_university: t("app_bar.services.inductive_university"),
            integrator_account: t("app_bar.services.integrator_account"),
            ignition_exchange: t("app_bar.services.ignition_exchange"),
            workspaces: t("app_bar.services.workspaces"),
          },
        }}
        workspacesUrl={window._acctConfig?.WORKSPACE_URL ?? ""}
        workspaceFeatureFlag={tsAccountWorkspaceVisibilityFe}
      />
      <Navigation withinMenu isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default AccountAppBar;
